import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../index'; // Assuming your root state is exported from store/index.ts
import type { AlgoliaDrink, SearchResultsResponse } from '@/types/firestore'; // Import AlgoliaDrink & SearchResultsResponse
import { searchDrinks } from '@/lib/api'; // Import the API helper. Changed from searchAll

// Define the type for brandSize, similar to FilterBox.tsx
export type SodaSearchBrandSizeType = 'all' | 'craft' | 'mass' | 'unknown';

// Define the sortable fields for sodas
export type SodaSortField = 'brandName' | 'drinkName' | 'rating'; // Added rating as per later thought

export interface SodaDisplayOptionsState {
  showFlavors: boolean;
  showAttributes: boolean;
  showStarRating: boolean;
  showReviewCount: boolean;
}

export interface SodaSearchFiltersState {
  searchTerm: string;
  brandSize: SodaSearchBrandSizeType;
  selectedBrandIds: string[];
  selectedFlavorIds: string[];
  selectedAttributeIds: string[];
  minRating: number | null;
  maxRating: number | null;
  sortField: SodaSortField;
  sortDirection: 'asc' | 'desc';
  
  // New fields for Algolia search results
  searchResults: AlgoliaDrink[];
  searchLoading: 'idle' | 'pending' | 'succeeded' | 'failed';
  searchError: string | null;
  currentPage: number;
  totalPages: number;
  totalResults: number;
  itemsPerPage: number; // New field for items per page
  displayOptions: SodaDisplayOptionsState; // New field for display options
}

export const sodaSearchFiltersInitialState: SodaSearchFiltersState = {
  searchTerm: '',
  brandSize: 'all',
  selectedBrandIds: [],
  selectedFlavorIds: [],
  selectedAttributeIds: [],
  minRating: null,
  maxRating: null,
  sortField: 'drinkName', // Default sort field
  sortDirection: 'asc',   // Default sort direction
  searchResults: [],
  searchLoading: 'idle',
  searchError: null,
  currentPage: 0,
  totalPages: 0,
  totalResults: 0,
  itemsPerPage: 12, // Default items per page
  displayOptions: { // Default display options
    showFlavors: true,
    showAttributes: true,
    showStarRating: true,
    showReviewCount: true,
  },
};

// This interface is for the arguments passed TO the thunk from the UI/dispatch
interface PerformSearchThunkArgs {
  query?: string;
  searchTerm?: string; // Support both query and searchTerm
  brandSize?: SodaSearchBrandSizeType | 'all';
  brandIds?: string[];
  selectedBrandIds?: string[]; // Support both brandIds and selectedBrandIds
  flavorIds?: string[];
  selectedFlavorIds?: string[]; // Support both flavorIds and selectedFlavorIds
  attributeIds?: string[];
  selectedAttributeIds?: string[]; // Support both attributeIds and selectedAttributeIds
  minRating?: number | null;
  maxRating?: number | null;
  sortField?: SodaSortField;
  sortDirection?: 'asc' | 'desc';
}

// This will be the structure returned by the searchAll API call when type is 'drinks'
// and then returned by the thunk.
interface AlgoliaDrinksSearchResponse {
    drinks: AlgoliaDrink[];
    page: number;
    totalPages: number;
    totalResults: number;
    // facets?: any; // if you use them
}

export const searchDrinksWithAlgolia = createAsyncThunk<
  AlgoliaDrinksSearchResponse, // Return type from the thunk
  PerformSearchThunkArgs,    // Arguments to the thunk
  { state: RootState; rejectValue: string }
>(
  'sodaSearch/searchDrinksAlgolia',
  async (filters, { rejectWithValue }) => {
    try {
      // Construct the filters specifically for the searchAll/searchDrinks API call
      const apiFilters: Partial<import('@/types/firestore').SearchFilters> = {
        query: filters.searchTerm || filters.query,
        brandSize: (filters.brandSize === 'all') ? undefined : filters.brandSize,
        brandIds: filters.selectedBrandIds || filters.brandIds,
        flavorIds: filters.selectedFlavorIds || filters.flavorIds,
        attributeIds: filters.selectedAttributeIds || filters.attributeIds,
        minRating: filters.minRating,
        maxRating: filters.maxRating,
        hitsPerPage: 1000, // Fetch up to 1000 results, assuming this is enough for "all"
      };
            
      // Use the searchDrinks function from lib/api.ts
      // The query string is part of apiFilters now
      const response: SearchResultsResponse = await searchDrinks(apiFilters.query, apiFilters);
      
      console.log("Search response:", response);

      // Handle the different possible response structures
      
      // Structure 1: The response itself has drinks, page, etc. at the root level
      if (Array.isArray(response.drinks)) {
        console.log("Found drinks array at root level, length:", response.drinks.length);
        // Type cast response to access page, totalPages, and totalResults
        const typedResponse = response as unknown as {
          drinks: AlgoliaDrink[];
          page: number; // This will likely be 0 from Algolia when hitsPerPage is large
          totalPages: number; // This will likely be 1 from Algolia
          totalResults: number;
        };
        
        return {
            drinks: typedResponse.drinks,
            page: 0, // For client-side pagination, the concept of a backend page is irrelevant here
            totalPages: 1, // Similarly, backend totalPages is 1 (all results)
            totalResults: typedResponse.totalResults || typedResponse.drinks.length,
        };
      } 
      // Structure 2: The response.drinks has hits, page, etc.
      else if (response.drinks && response.drinks.hits && Array.isArray(response.drinks.hits)) {
        console.log("Found drinks.hits array, length:", response.drinks.hits.length);
        return {
            drinks: response.drinks.hits,
            page: 0, // All results fetched
            totalPages: 1, // All results fetched
            totalResults: response.drinks.totalResults,
        };
      } 
      // Structure 3: The response is an array of drinks directly
      else if (Array.isArray(response)) {
        console.log("Response is directly an array, length:", response.length);
        return {
          drinks: response as AlgoliaDrink[],
          page: 0, // All results fetched
          totalPages: 1, // All results fetched
          totalResults: response.length
        };
      } 
      // No valid structure found
      else {
        console.error('Drink search results not found in API response or response structure is unexpected:', response);
        throw new Error('Drink search results not found in API response or response structure is unexpected');
      }
    } catch (error: any) {
      console.error("Error searching drinks with Algolia via API helper:", error);
      return rejectWithValue(error.message || 'Failed to search drinks using API helper');
    }
  }
);

const sodaSearchFiltersSlice = createSlice({
  name: 'sodaSearchFilters',
  initialState: sodaSearchFiltersInitialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.currentPage = 0; // Reset page when search term changes
    },
    setBrandSize: (state, action: PayloadAction<SodaSearchBrandSizeType>) => {
      state.brandSize = action.payload;
      state.currentPage = 0;
    },
    toggleBrandId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedBrandIds.includes(id)) {
        state.selectedBrandIds = state.selectedBrandIds.filter(bId => bId !== id);
      } else {
        state.selectedBrandIds.push(id);
      }
      state.currentPage = 0;
    },
    setSelectedBrandIds: (state, action: PayloadAction<string[]>) => {
      state.selectedBrandIds = action.payload;
      state.currentPage = 0;
    },
    toggleFlavorId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedFlavorIds.includes(id)) {
        state.selectedFlavorIds = state.selectedFlavorIds.filter(fId => fId !== id);
      } else {
        state.selectedFlavorIds.push(id);
      }
      state.currentPage = 0;
    },
    setSelectedFlavorIds: (state, action: PayloadAction<string[]>) => {
      state.selectedFlavorIds = action.payload;
      state.currentPage = 0;
    },
    toggleAttributeId: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (state.selectedAttributeIds.includes(id)) {
        state.selectedAttributeIds = state.selectedAttributeIds.filter(aId => aId !== id);
      } else {
        state.selectedAttributeIds.push(id);
      }
      state.currentPage = 0;
    },
    setSelectedAttributeIds: (state, action: PayloadAction<string[]>) => {
      state.selectedAttributeIds = action.payload;
      state.currentPage = 0;
    },
    setMinRating: (state, action: PayloadAction<number | null>) => {
      state.minRating = action.payload;
      state.currentPage = 0;
    },
    setMaxRating: (state, action: PayloadAction<number | null>) => {
      state.maxRating = action.payload;
      state.currentPage = 0;
    },
    setSortField: (state, action: PayloadAction<SodaSortField>) => {
      state.sortField = action.payload;
      // Sorting is client-side for now, so no page reset needed unless server handles sort
    },
    toggleSortDirection: (state) => {
      state.sortDirection = state.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
        state.currentPage = action.payload;
    },
    setItemsPerPage: (state, action: PayloadAction<number>) => { // New reducer for itemsPerPage
      state.itemsPerPage = action.payload;
      state.currentPage = 0; // Reset page when itemsPerPage changes
    },
    toggleDisplayOption: (state, action: PayloadAction<keyof SodaDisplayOptionsState>) => { // New reducer for display options
      const key = action.payload;
      state.displayOptions[key] = !state.displayOptions[key];
    },
    resetSodaSearchFilters: (state) => {
      // Keep sort order, display options, and itemsPerPage, reset everything else
      const sortField = state.sortField;
      const sortDirection = state.sortDirection;
      const itemsPerPage = state.itemsPerPage;
      const displayOptions = state.displayOptions;
      Object.assign(state, sodaSearchFiltersInitialState);
      state.sortField = sortField;
      state.sortDirection = sortDirection;
      state.itemsPerPage = itemsPerPage; // Persist itemsPerPage on reset
      state.displayOptions = displayOptions; // Persist displayOptions on reset
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDrinksWithAlgolia.pending, (state) => {
        state.searchLoading = 'pending';
        state.searchError = null;
      })
      .addCase(searchDrinksWithAlgolia.fulfilled, (state, action) => {
        state.searchLoading = 'succeeded';
        // Make sure searchResults is always an array of drinks
        state.searchResults = Array.isArray(action.payload.drinks) ? action.payload.drinks : [];
        state.currentPage = 0; // Reset to first page for client-side pagination
        state.totalResults = action.payload.totalResults;
      })
      .addCase(searchDrinksWithAlgolia.rejected, (state, action) => {
        state.searchLoading = 'failed';
        state.searchError = action.payload as string;
        state.searchResults = []; // Clear results on error
        state.totalPages = 0;
        state.totalResults = 0;
      });
  },
});

export const {
  setSearchTerm,
  setBrandSize,
  toggleBrandId,
  setSelectedBrandIds,
  toggleFlavorId,
  setSelectedFlavorIds,
  toggleAttributeId,
  setSelectedAttributeIds,
  setMinRating,
  setMaxRating,
  setSortField,
  toggleSortDirection,
  setSortDirection,
  setCurrentPage,
  resetSodaSearchFilters,
  setItemsPerPage, // Export new action
  toggleDisplayOption, // Export new action
} = sodaSearchFiltersSlice.actions;

// Selectors
export const selectSodaSearchFilters = (state: RootState): SodaSearchFiltersState => state.sodaSearchFilters;
export const selectSodaSearchTerm = (state: RootState): string => state.sodaSearchFilters.searchTerm;
export const selectSodaSearchBrandSize = (state: RootState): SodaSearchBrandSizeType => state.sodaSearchFilters.brandSize;
export const selectSodaSearchSelectedBrandIds = (state: RootState): string[] => state.sodaSearchFilters.selectedBrandIds;
export const selectSodaSearchSelectedFlavorIds = (state: RootState): string[] => state.sodaSearchFilters.selectedFlavorIds;
export const selectSodaSearchSelectedAttributeIds = (state: RootState): string[] => state.sodaSearchFilters.selectedAttributeIds;
export const selectSodaSearchMinRating = (state: RootState): number | null => state.sodaSearchFilters.minRating;
export const selectSodaSearchMaxRating = (state: RootState): number | null => state.sodaSearchFilters.maxRating;
export const selectSodaSearchSortField = (state: RootState): SodaSortField => state.sodaSearchFilters.sortField;
export const selectSodaSearchSortDirection = (state: RootState): 'asc' | 'desc' => state.sodaSearchFilters.sortDirection;
export const selectSodaSearchResults = (state: RootState): AlgoliaDrink[] => state.sodaSearchFilters.searchResults;
export const selectSodaSearchLoading = (state: RootState): 'idle' | 'pending' | 'succeeded' | 'failed' => state.sodaSearchFilters.searchLoading;
export const selectSodaSearchError = (state: RootState): string | null => state.sodaSearchFilters.searchError;
export const selectSodaSearchCurrentPage = (state: RootState): number => state.sodaSearchFilters.currentPage;
export const selectSodaSearchTotalPages = (state: RootState): number => state.sodaSearchFilters.totalPages;
export const selectSodaSearchTotalResults = (state: RootState): number => state.sodaSearchFilters.totalResults;
export const selectItemsPerPage = (state: RootState): number => state.sodaSearchFilters.itemsPerPage; // New selector
export const selectDisplayOptions = (state: RootState): SodaDisplayOptionsState => state.sodaSearchFilters.displayOptions; // New selector

export default sodaSearchFiltersSlice.reducer; 